/**
 * Support cases (user-facing: Tickets) — the shared vocabulary of the new case
 * domain.
 *
 * A support case is a first-class row, not a lens over a conversation: its
 * stage, owner and visibility are columns the server owns, and this module is
 * the single client-side reader of them. Everything here is presentational
 * mapping (category -> colour/icon/label key) plus the two presenters the list,
 * the workroom and the kit all render from, so a stage never looks like one
 * thing in the table and another in the header.
 */
import { formatDistanceToNow, isValid, parseISO } from 'date-fns';

import { shortTimestamp } from 'shared/helpers/timeHelper';

// The fixed system categories every stage maps to, whatever a workspace calls
// its stages. Mirrors the `category` enum on `case_stages`.
export const STAGE_CATEGORY = {
  SUBMITTED: 'submitted',
  IN_PROGRESS: 'in_progress',
  WAITING: 'waiting',
  RESOLVED: 'resolved',
};

// `color` is a `components-next` Label palette key, not a class string: the kit
// renders through the shared primitives so no case surface carries its own copy
// of a colour class.
export const STAGE_CATEGORY_META = {
  [STAGE_CATEGORY.SUBMITTED]: {
    labelKey: 'SUPPORT_CASES.STAGE_CATEGORIES.SUBMITTED',
    icon: 'i-lucide-circle-dot',
    color: 'blue',
    iconClass: 'text-n-blue-10',
    dotClass: 'bg-n-blue-9',
  },
  [STAGE_CATEGORY.IN_PROGRESS]: {
    labelKey: 'SUPPORT_CASES.STAGE_CATEGORIES.IN_PROGRESS',
    icon: 'i-lucide-circle-dashed',
    color: 'amber',
    iconClass: 'text-n-amber-10',
    dotClass: 'bg-n-amber-9',
  },
  [STAGE_CATEGORY.WAITING]: {
    labelKey: 'SUPPORT_CASES.STAGE_CATEGORIES.WAITING',
    icon: 'i-lucide-circle-pause',
    color: 'iris',
    iconClass: 'text-n-iris-10',
    dotClass: 'bg-n-iris-9',
  },
  [STAGE_CATEGORY.RESOLVED]: {
    labelKey: 'SUPPORT_CASES.STAGE_CATEGORIES.RESOLVED',
    icon: 'i-lucide-circle-check',
    color: 'teal',
    iconClass: 'text-n-teal-10',
    dotClass: 'bg-n-teal-9',
  },
};

// An unknown category (a stage created by a newer server than this bundle)
// renders neutral rather than blank.
export const NEUTRAL_STAGE_META = {
  labelKey: 'SUPPORT_CASES.STAGE_CATEGORIES.UNKNOWN',
  icon: 'i-lucide-circle',
  color: 'slate',
  iconClass: 'text-n-slate-10',
  dotClass: 'bg-n-slate-9',
};

export const stageCategoryMeta = category =>
  STAGE_CATEGORY_META[category] || NEUTRAL_STAGE_META;

/**
 * A stage carries two labels: the internal `name` the team works with, and the
 * optional `customerLabel` whose presence is what makes the stage
 * customer-visible. Only the progress feed and the settings preview ask for the
 * customer-facing one.
 *
 * @param {{name: string, customerLabel: ?string}} stage
 * @param {boolean} customerFacing
 * @returns {string}
 */
export const stageLabel = (stage, customerFacing = false) => {
  if (!stage) return '';
  return customerFacing ? stage.customerLabel || stage.name : stage.name;
};

// The shapes a ticket type can ask for. Mirrors the `field_type` enum on
// `case_field_definitions`; two of them are backed by a list of choices, which
// is the only thing the settings editor and the workroom panel branch on.
export const FIELD_TYPE = {
  TEXT: 'text',
  NUMBER: 'number',
  DATE: 'date',
  SELECT: 'select',
  MULTI_SELECT: 'multi_select',
  BOOLEAN: 'boolean',
  LINK: 'link',
};

export const FIELD_TYPE_META = {
  [FIELD_TYPE.TEXT]: {
    labelKey: 'SUPPORT_CASES.FIELD_TYPES.TEXT',
    icon: 'i-lucide-type',
  },
  [FIELD_TYPE.NUMBER]: {
    labelKey: 'SUPPORT_CASES.FIELD_TYPES.NUMBER',
    icon: 'i-lucide-hash',
  },
  [FIELD_TYPE.DATE]: {
    labelKey: 'SUPPORT_CASES.FIELD_TYPES.DATE',
    icon: 'i-lucide-calendar',
  },
  [FIELD_TYPE.SELECT]: {
    labelKey: 'SUPPORT_CASES.FIELD_TYPES.SELECT',
    icon: 'i-lucide-list',
  },
  [FIELD_TYPE.MULTI_SELECT]: {
    labelKey: 'SUPPORT_CASES.FIELD_TYPES.MULTI_SELECT',
    icon: 'i-lucide-list-checks',
  },
  [FIELD_TYPE.BOOLEAN]: {
    labelKey: 'SUPPORT_CASES.FIELD_TYPES.BOOLEAN',
    icon: 'i-lucide-toggle-left',
  },
  [FIELD_TYPE.LINK]: {
    labelKey: 'SUPPORT_CASES.FIELD_TYPES.LINK',
    icon: 'i-lucide-link',
  },
};

// A field type this bundle has never heard of (a newer server) is named by its
// own value rather than rendering as a gap in the list.
export const fieldTypeMeta = fieldType =>
  FIELD_TYPE_META[fieldType] || {
    labelKey: 'SUPPORT_CASES.FIELD_TYPES.UNKNOWN',
    icon: 'i-lucide-circle',
  };

// Options are the whole point of these two and meaningless on the rest, which
// is exactly the rule the server enforces — the editor only mirrors it.
export const isOptionBackedField = fieldType =>
  fieldType === FIELD_TYPE.SELECT || fieldType === FIELD_TYPE.MULTI_SELECT;

// Owner is an individual OR a team, exactly one, or nobody.
export const OWNER_TYPE = {
  USER: 'user',
  TEAM: 'team',
};

/**
 * Flattens the two nullable owner columns into the one shape the chip renders.
 * `null` means unassigned, which is a real state, not a missing value.
 *
 * @param {{assignee: ?Object, assigneeTeam: ?Object}} supportCase
 * @returns {?{type: string, id: number, name: string, thumbnail: string}}
 */
export const presentOwner = ({ assignee, assigneeTeam } = {}) => {
  if (assignee) {
    return {
      type: OWNER_TYPE.USER,
      id: assignee.id,
      name: assignee.name || assignee.availableName || '',
      thumbnail: assignee.thumbnail || '',
    };
  }
  if (assigneeTeam) {
    return {
      type: OWNER_TYPE.TEAM,
      id: assigneeTeam.id,
      name: assigneeTeam.name || '',
      thumbnail: '',
    };
  }
  return null;
};

// Who can see a case, computed on the server from its type's sharing mode. The
// client only renders the verdict; it never derives one.
export const VISIBILITY = {
  WORKSPACE: 'workspace',
  TEAMS: 'teams',
  PRIVATE: 'private',
};

export const VISIBILITY_META = {
  [VISIBILITY.WORKSPACE]: {
    labelKey: 'SUPPORT_CASES.VISIBILITY.WORKSPACE',
    descriptionKey: 'SUPPORT_CASES.VISIBILITY.WORKSPACE_DESCRIPTION',
    icon: 'i-lucide-globe',
  },
  [VISIBILITY.TEAMS]: {
    labelKey: 'SUPPORT_CASES.VISIBILITY.TEAMS',
    descriptionKey: 'SUPPORT_CASES.VISIBILITY.TEAMS_DESCRIPTION',
    icon: 'i-lucide-users',
  },
  [VISIBILITY.PRIVATE]: {
    labelKey: 'SUPPORT_CASES.VISIBILITY.PRIVATE',
    descriptionKey: 'SUPPORT_CASES.VISIBILITY.PRIVATE_DESCRIPTION',
    icon: 'i-lucide-lock',
  },
};

// Deny-by-default reading: an unrecognised mode is shown as the most restrictive
// one, so a badge can never overstate who sees a case.
export const visibilityMeta = mode =>
  VISIBILITY_META[mode] || VISIBILITY_META[VISIBILITY.PRIVATE];

/**
 * Who did it. `case_activities.actor` and `case_comments.author` are the same
 * polymorphic, nullable slot: a person's change carries a `User`, a rule's
 * change carries the `AutomationRule` that made it, and a scheduled sweep
 * carries nobody at all — which is a fact about the event, not a row the client
 * failed to load.
 *
 * The feed reads that slot as "is this a person" rather than as "is this an
 * AutomationRule", so a future non-user actor renders as machine-made instead of
 * quietly borrowing a person's name, avatar and side of the row.
 */
export const ACTOR_TYPE = {
  USER: 'User',
  AUTOMATION_RULE: 'AutomationRule',
};

export const isUserActor = actor => actor?.type === ACTOR_TYPE.USER;

// An actor with no type at all is not identifiable as anything, so it is not claimed as an
// automation either — that is the actor-less case, and it reads as the system.
export const isAutomationActor = actor =>
  Boolean(actor?.type) && !isUserActor(actor);

/**
 * The name in front of a feed line.
 *
 * A person is named by the short form they go by. A rule is named by the rule,
 * because "which rule did this" is the first thing an admin asks of an automated
 * change — and a rule the server sent without a name still says it was an
 * automation rather than leaving a gap that reads as if nobody touched the
 * ticket.
 *
 * @param {?{type: ?string, name: ?string, availableName: ?string}} actor
 * @param {Function} t vue-i18n's translate
 * @returns {string}
 */
export const actorName = (actor, t) => {
  if (isAutomationActor(actor)) {
    return (
      actor.name ||
      actor.availableName ||
      t('SUPPORT_CASES.DETAIL.ACTIVITY_AUTOMATION')
    );
  }
  return (
    actor?.availableName ||
    actor?.name ||
    t('SUPPORT_CASES.DETAIL.ACTIVITY_SYSTEM')
  );
};

/**
 * The audit trail's vocabulary. `case_activities.event_type` stores the event
 * catalogue's string verbatim (events.md §3), so this map is keyed by those
 * values and nothing on the client re-derives what happened.
 *
 * An event this bundle has no line for still renders — as its own type, with a
 * neutral icon — because a trail that silently drops entries is worse than one
 * that names an entry it cannot phrase.
 */
export const ACTIVITY_META = {
  'support_case.created': {
    labelKey: 'SUPPORT_CASES.ACTIVITY.CREATED',
    icon: 'i-lucide-plus',
  },
  'support_case.updated': {
    labelKey: 'SUPPORT_CASES.ACTIVITY.UPDATED',
    icon: 'i-lucide-pencil',
  },
  'support_case.stage_changed': {
    labelKey: 'SUPPORT_CASES.ACTIVITY.STAGE_CHANGED',
    icon: 'i-lucide-git-commit-horizontal',
  },
  'support_case.assigned': {
    labelKey: 'SUPPORT_CASES.ACTIVITY.ASSIGNED',
    icon: 'i-lucide-user-round',
  },
  'support_case.archived': {
    labelKey: 'SUPPORT_CASES.ACTIVITY.ARCHIVED',
    icon: 'i-lucide-archive',
  },
  'support_case.unarchived': {
    labelKey: 'SUPPORT_CASES.ACTIVITY.UNARCHIVED',
    icon: 'i-lucide-archive-restore',
  },
  'support_case.comment_created': {
    labelKey: 'SUPPORT_CASES.ACTIVITY.COMMENT_CREATED',
    icon: 'i-lucide-message-square',
  },
  'support_case.comment_updated': {
    labelKey: 'SUPPORT_CASES.ACTIVITY.COMMENT_UPDATED',
    icon: 'i-lucide-message-square',
  },
  'support_case.comment_archived': {
    labelKey: 'SUPPORT_CASES.ACTIVITY.COMMENT_ARCHIVED',
    icon: 'i-lucide-message-square-off',
  },
  'support_case.task_created': {
    labelKey: 'SUPPORT_CASES.ACTIVITY.TASK_CREATED',
    icon: 'i-lucide-list-checks',
  },
  'support_case.task_toggled': {
    labelKey: 'SUPPORT_CASES.ACTIVITY.TASK_TOGGLED',
    icon: 'i-lucide-list-checks',
  },
  'support_case.task_archived': {
    labelKey: 'SUPPORT_CASES.ACTIVITY.TASK_ARCHIVED',
    icon: 'i-lucide-list-checks',
  },
  'support_case.conversation_linked': {
    labelKey: 'SUPPORT_CASES.ACTIVITY.CONVERSATION_LINKED',
    icon: 'i-lucide-link',
  },
  'support_case.conversation_unlinked': {
    labelKey: 'SUPPORT_CASES.ACTIVITY.CONVERSATION_UNLINKED',
    icon: 'i-lucide-unlink',
  },
  'support_case.progress_published': {
    labelKey: 'SUPPORT_CASES.ACTIVITY.PROGRESS_PUBLISHED',
    icon: 'i-lucide-send',
  },
  // Written by the legacy backfill rather than by the event bus
  // (`SupportCases::LegacyMigration::Backfill`), and every ticket the cutover
  // moves carries exactly one — so after a migration this is the most common
  // entry in the trail, and the one line it must not be missing.
  'support_case.migrated': {
    labelKey: 'SUPPORT_CASES.ACTIVITY.MIGRATED',
    icon: 'i-lucide-import',
  },
};

export const activityMeta = eventType =>
  ACTIVITY_META[eventType] || {
    labelKey: 'SUPPORT_CASES.ACTIVITY.UNKNOWN',
    icon: 'i-lucide-circle',
  };

/**
 * The trail's one line of prose per entry.
 *
 * The activity payload is the event's own body (`{ changes, metadata }`, see
 * `SupportCaseActivityListener`): `changes` holds `[before, after]` pairs of the
 * columns that moved, `metadata` the labels and ids of whatever the event was
 * about. Two events carry enough to say what actually happened, so they say it:
 *
 *   stage_changed  metadata carries both stage *categories*, which is the one
 *                  vocabulary every workflow maps onto — the stage names
 *                  themselves are not in the payload and refetching a renamed
 *                  stage would rewrite history.
 *   assigned       `changes` carries the owner ids; a name is only shown when
 *                  the id resolves against the lists the screen already holds.
 *                  An id this session cannot name falls back to the generic
 *                  line rather than printing a number at the reader.
 *
 * Everything else — including an event type this bundle has never heard of —
 * keeps the generic phrasing, because a trail that drops what it cannot phrase
 * is worse than one that admits it.
 *
 * @param {Object} activity the camelised activity row
 * @param {Function} t vue-i18n's translate
 * @param {{users: Array, teams: Array}} directory the lists a name may resolve
 *   against; both default to empty, which is the "not resolvable" case
 * @returns {string}
 */
export const activityLabel = (activity, t, { users = [], teams = [] } = {}) => {
  const meta = activityMeta(activity?.eventType);
  const generic = t(meta.labelKey, { type: activity?.eventType });
  const { changes = {}, metadata = {} } = activity?.payload || {};

  if (activity?.eventType === 'support_case.stage_changed') {
    const { previousStageCategory: from, stageCategory: to } = metadata;
    if (!from || !to || from === to) return generic;
    return t('SUPPORT_CASES.ACTIVITY.STAGE_CHANGED_DETAIL', {
      from: t(stageCategoryMeta(from).labelKey),
      to: t(stageCategoryMeta(to).labelKey),
    });
  }

  if (activity?.eventType === 'support_case.assigned') {
    // `[before, after]`; the owner slot holds a person, a team or nobody, so
    // both sides being empty is a real unassignment rather than missing data.
    const userId = changes.assigneeUserId?.[1] ?? null;
    const teamId = changes.assigneeTeamId?.[1] ?? null;
    if (!userId && !teamId) {
      return changes.assigneeUserId || changes.assigneeTeamId
        ? t('SUPPORT_CASES.ACTIVITY.ASSIGNED_CLEARED')
        : generic;
    }

    const owner = userId
      ? users.find(user => String(user.id) === String(userId))
      : teams.find(team => String(team.id) === String(teamId));
    if (!owner) return generic;

    return t('SUPPORT_CASES.ACTIVITY.ASSIGNED_TO', {
      name: owner.name || owner.availableName || '',
    });
  }

  return generic;
};

// Mirrors the `priority` enum on `support_cases`. Nullable on the row: "no
// priority set" is a real state, and surfaces render nothing for it rather
// than inventing a default.
export const CASE_PRIORITY = {
  URGENT: 'urgent',
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
};

// `color` is a `components-next` Label palette key, same contract as the stage
// meta above, so a priority chip is built from the same primitive as a stage
// pill.
export const CASE_PRIORITY_META = {
  [CASE_PRIORITY.URGENT]: {
    labelKey: 'SUPPORT_CASES.PRIORITY.URGENT',
    icon: 'i-woot-priority-urgent',
    color: 'ruby',
  },
  [CASE_PRIORITY.HIGH]: {
    labelKey: 'SUPPORT_CASES.PRIORITY.HIGH',
    icon: 'i-woot-priority-high',
    color: 'amber',
  },
  [CASE_PRIORITY.MEDIUM]: {
    labelKey: 'SUPPORT_CASES.PRIORITY.MEDIUM',
    icon: 'i-woot-priority-medium',
    color: 'blue',
  },
  [CASE_PRIORITY.LOW]: {
    labelKey: 'SUPPORT_CASES.PRIORITY.LOW',
    icon: 'i-woot-priority-low',
    color: 'slate',
  },
};

// Unlike stages, an unknown priority renders nothing at all: a chip is an
// accent, not a column, and a neutral "Priority" chip would only add noise.
export const casePriorityMeta = priority =>
  CASE_PRIORITY_META[priority] || null;

// The owner scopes the list offers. One word for "mine" everywhere: My work.
export const OWNER_SCOPE = {
  ALL: 'all',
  MINE: 'me',
  UNASSIGNED: 'unassigned',
};

/**
 * Whether a case has out-stayed its stage's SLA. The rule lives on the stage
 * (`slaMinutes`, null = no rule) and the clock is `stageEnteredAt`, which
 * `ChangeStage` restamps on every move — so the breach is a pure client-side
 * comparison; no scheduler runs on the server.
 *
 * @param {{stageEnteredAt: ?string, caseStage: ?{slaMinutes: ?number}}} supportCase
 * @returns {boolean}
 */
export const isOverdueInStage = supportCase => {
  const slaMinutes = supportCase?.caseStage?.slaMinutes;
  const enteredAt = supportCase?.stageEnteredAt;
  if (!slaMinutes || !enteredAt) return false;

  const entered = parseISO(enteredAt);
  if (!isValid(entered)) return false;
  return Date.now() > entered.getTime() + slaMinutes * 60 * 1000;
};

// Two notes by the same author inside this window read as one turn of a
// conversation, so the second drops its avatar and author line.
export const GROUPING_WINDOW_MS = 5 * 60 * 1000;

// Comment lifecycle events are already visible as the comments themselves; a
// system line beside every bubble would say the same thing twice.
export const FEED_HIDDEN_EVENT_TYPES = [
  'support_case.comment_created',
  'support_case.comment_updated',
  'support_case.comment_archived',
];

// A row whose timestamp cannot be read sorts last rather than jumping to the
// top of a feed it has no claim on.
const feedTime = createdAt => {
  const at = Date.parse(createdAt);
  return Number.isNaN(at) ? Number.MAX_SAFE_INTEGER : at;
};

const rowId = entry => Number(entry.comment?.id ?? entry.activity?.id) || 0;

const commentAuthorKey = comment =>
  `${comment?.author?.type || ''}:${comment?.author?.id ?? ''}`;

/**
 * The workroom's one feed: the internal notes and the audit trail read as a
 * single chronology, because the team reasons about a ticket in the order
 * things happened, not in two parallel columns.
 *
 * Comments arrive oldest-first and activities newest-first, so the merge sorts
 * rather than concatenating. Alignment and grouping are decided here, on the
 * whole feed, instead of in the bubble: a bubble cannot see the entry before it.
 *
 * @param {Array} comments camelised comment rows
 * @param {Array} activities camelised activity rows
 * @param {{currentUserId: ?(number|string)}} options the signed-in agent, whose
 *   own notes align to the end of the row
 * @returns {Array<Object>} entries, oldest first
 */
export const mergeCaseFeed = (
  comments = [],
  activities = [],
  { currentUserId = null } = {}
) => {
  const authorNameById = new Map(
    comments.map(comment => [
      comment.id,
      comment.author?.availableName || comment.author?.name || '',
    ])
  );

  const commentEntries = comments.map(comment => ({
    kind: 'comment',
    id: `comment-${comment.id}`,
    at: feedTime(comment.createdAt),
    comment,
    // The author has to be a person before the id is worth comparing: a rule's note carries
    // the rule's id, and rule 7 is not agent 7 — ids only mean the same thing inside one
    // table. So an automated note can never end up on the signed-in agent's side of the row.
    align:
      isUserActor(comment.author) &&
      currentUserId != null &&
      String(comment.author.id) === String(currentUserId)
        ? 'end'
        : 'start',
    isAutomation: isAutomationActor(comment.author),
    isGrouped: false,
    replyToName: authorNameById.get(comment.parentId) || '',
  }));

  const activityEntries = activities
    .filter(activity => !FEED_HIDDEN_EVENT_TYPES.includes(activity.eventType))
    .map(activity => ({
      kind: 'activity',
      id: `activity-${activity.id}`,
      at: feedTime(activity.createdAt),
      activity,
      isAutomation: isAutomationActor(activity.actor),
    }));

  const entries = [...commentEntries, ...activityEntries].sort((a, b) => {
    if (a.at !== b.at) return a.at - b.at;
    // A note and the event it caused share a timestamp; the note came first.
    if (a.kind !== b.kind) return a.kind === 'comment' ? -1 : 1;
    return rowId(a) - rowId(b);
  });

  entries.forEach((entry, index) => {
    if (entry.kind !== 'comment') return;
    const previous = entries[index - 1];
    entry.isGrouped =
      previous?.kind === 'comment' &&
      commentAuthorKey(previous.comment) === commentAuthorKey(entry.comment) &&
      previous.align === entry.align &&
      entry.at - previous.at <= GROUPING_WINDOW_MS;
  });

  return entries;
};

export const SORT_OPTION = {
  LAST_ACTIVITY: 'last_activity',
  CREATED_AT: 'created_at',
};

/**
 * The list's "last activity" column. Case timestamps are ISO 8601 strings (the
 * API contract), not the unix seconds the rest of `timeHelper` takes, so the
 * parse happens here rather than at every call site. Renders in the short
 * scanning form the mockup uses — `2h`, `1d` — not a sentence.
 *
 * @param {?string} timestamp ISO 8601, or null when unset
 * @returns {string} empty string for a missing or unparseable timestamp
 */
export const lastActivityLabel = timestamp => {
  if (!timestamp) return '';
  const date = parseISO(timestamp);
  if (!isValid(date)) return '';
  // `shortTimestamp` strips the "about"/"over"/"almost" qualifiers but leaves
  // the space they sat in behind, so the trim is the caller's to do.
  return shortTimestamp(formatDistanceToNow(date, { addSuffix: true })).trim();
};
